import { DateTime } from "luxon";

import { TandemEvent, ValidTandemEvent } from "@/lib/drivers/tandem/commands/Event";
import { ControlIQPumpControlModeChangeEvent, LID_AA_PCM_CHANGE } from "@/lib/drivers/tandem/events/LID_AA_PCM_CHANGE";
import {
  ControlIQUserModeChangeEvent,
  LID_AA_USER_MODE_CHANGE,
} from "@/lib/drivers/tandem/events/LID_AA_USER_MODE_CHANGE";
import { CannulaFilledEvent, LID_CANNULA_FILLED } from "@/lib/drivers/tandem/events/LID_CANNULA_FILLED";
import { CartridgeFilledEvent, LID_CARTRIDGE_FILLED } from "@/lib/drivers/tandem/events/LID_CARTRIDGE_FILLED";
import { DailyBasalEvent, LID_DAILY_BASAL } from "@/lib/drivers/tandem/events/LID_DAILY_BASAL";
import { LID_NEW_DAY, NewDayEvent } from "@/lib/drivers/tandem/events/LID_NEW_DAY";
import { LID_TUBING_FILLED, TubingFilledEvent } from "@/lib/drivers/tandem/events/LID_TUBING_FILLED";
import { TandemData } from "@/lib/drivers/tandem/TandemTypes";
import { TandemTransformer } from "@/lib/drivers/tandem/transformers/TandemTransformer";
import { PumpEventContentDTO, PumpEventDTO } from "@/models/PumpEventModel";
import { DateUtils } from "@/utils/dateUtils";

type PumpEvent = ValidTandemEvent<CannulaFilledEvent | TubingFilledEvent | CartridgeFilledEvent>;

type PumpModeEvent = ValidTandemEvent<ControlIQPumpControlModeChangeEvent>;
type UserModeEvent = ValidTandemEvent<ControlIQUserModeChangeEvent>;
type DayBasalEvent = ValidTandemEvent<DailyBasalEvent | NewDayEvent>;

export class TandemPumpEventsTransformer implements TandemTransformer<PumpEventDTO[]> {
  _is_cannula_filled_event = (event: TandemEvent<unknown>): event is ValidTandemEvent<CannulaFilledEvent> =>
    event.id === LID_CANNULA_FILLED.value && event.valid;

  _is_tubing_filled_event = (event: TandemEvent<unknown>): event is ValidTandemEvent<TubingFilledEvent> =>
    event.id === LID_TUBING_FILLED.value && event.valid;

  _is_cartridge_filled_event = (event: TandemEvent<unknown>): event is ValidTandemEvent<CartridgeFilledEvent> =>
    event.id === LID_CARTRIDGE_FILLED.value && event.valid;

  _is_daily_basal_event = (event: TandemEvent<unknown>): event is ValidTandemEvent<DailyBasalEvent> =>
    event.id === LID_DAILY_BASAL.value && event.valid;

  _is_new_day_event = (event: TandemEvent<unknown>): event is ValidTandemEvent<NewDayEvent> =>
    event.id === LID_NEW_DAY.value && event.valid;

  _is_user_mode_change_event = (event: TandemEvent<unknown>): event is ValidTandemEvent<ControlIQUserModeChangeEvent> =>
    event.id === LID_AA_USER_MODE_CHANGE.value && event.valid;

  _is_control_mode_change_event = (
    event: TandemEvent<unknown>,
  ): event is ValidTandemEvent<ControlIQPumpControlModeChangeEvent> =>
    event.id === LID_AA_PCM_CHANGE.value && event.valid;

  _map_event_category = (event: PumpEvent): PumpEventContentDTO => {
    if (this._is_cannula_filled_event(event)) {
      return {
        category: "cannula_filled",
        data: {
          prime_size: event.payload.primeSize,
        },
      };
    } else if (this._is_tubing_filled_event(event)) {
      return {
        category: "tubing_filled",
        data: {
          prime_size: event.payload.primeSize ?? 0,
        },
      };
    } else if (this._is_cartridge_filled_event(event)) {
      return {
        category: "cartridge_filled",
        data: {
          insulin_actual: event.payload.insulinActual ?? 0,
          insulin_display: event.payload.insulinDisplay ?? 0,
        },
      };
    }
    // Can't happen
    throw new Error("Invalid event");
  };

  get_pump_events = (pumpEvents: PumpEvent[]): PumpEventDTO[] => {
    const result: PumpEventDTO[] = [];
    for (const event of pumpEvents) {
      result.push({
        sequence_id: event.eventSequenceNum,
        date: event.timestamp.toISO() ?? "",
        ...this._map_event_category(event),
      });
    }

    return result;
  };

  get_daily_basal = (pumpEvents: DayBasalEvent[]): PumpEventDTO[] => {
    // Find new day events and take the daily basal from the previous daily basal event
    const events = pumpEvents.sort((a, b) => DateUtils.compareDateTimes(a.timestamp, b.timestamp));
    const result: PumpEventDTO[] = [];
    let previousBasal: ValidTandemEvent<DailyBasalEvent> | undefined = undefined;
    for (const event of events) {
      if (this._is_new_day_event(event)) {
        if (
          previousBasal &&
          previousBasal.timestamp.diff(event.timestamp, "hour").hours < 3 &&
          previousBasal.payload.dailyTotalBasal > 0
        ) {
          result.push({
            sequence_id: previousBasal.eventSequenceNum,
            date: previousBasal.timestamp.toISO() ?? "",
            category: "daily_basal",
            data: {
              daily_total_basal: previousBasal.payload.dailyTotalBasal,
            },
          });
        }
      } else if (this._is_daily_basal_event(event)) {
        previousBasal = event;
      }
    }
    return result;
  };

  _fill_empty_days_with_events = (
    result: PumpEventDTO[],
    nextDate: DateTime,
    previousEvent: PumpModeEvent | undefined,
  ) => {
    if (previousEvent && previousEvent.payload.currentPCM) {
      let eventDay = previousEvent.timestamp.startOf("day").plus({ day: 1 });
      let previousMode = previousEvent.payload.previousPCM;
      while (eventDay < nextDate) {
        result.push({
          category: "closed_loop_pump_mode_changed",
          data: {
            mode: previousEvent.payload.currentPCM,
            previous_mode: previousMode,
          },
          date: eventDay.toISO() ?? "",
          sequence_id: previousEvent.eventSequenceNum,
        });
        previousMode = previousEvent.payload.currentPCM;
        eventDay = eventDay.plus({ day: 1 });
      }
    }
  };

  get_closed_loop_pump_mode = (modeEvents: PumpModeEvent[], latestEventDate: DateTime) => {
    const events = modeEvents.sort((a, b) => DateUtils.compareDateTimes(a.timestamp, b.timestamp));

    const result: PumpEventDTO[] = [];
    let previousEvent: PumpModeEvent | undefined = undefined;
    for (const event of events) {
      if (!previousEvent && event.payload.previousPCM) {
        result.push({
          category: "closed_loop_pump_mode_changed",
          data: {
            mode: event.payload.previousPCM,
            previous_mode: undefined,
          },
          date: event.timestamp.startOf("day").toISO() ?? "",
          sequence_id: events[0].eventSequenceNum,
        });
      }
      this._fill_empty_days_with_events(result, event.timestamp, previousEvent);
      if (event.payload.currentPCM) {
        result.push({
          category: "closed_loop_pump_mode_changed",
          data: {
            mode: event.payload.currentPCM,
            previous_mode: event.payload.previousPCM,
          },
          date: event.timestamp.toISO() ?? "",
          sequence_id: event.eventSequenceNum,
        });
      }
      previousEvent = event;
    }
    this._fill_empty_days_with_events(result, latestEventDate, previousEvent);
    if (previousEvent && previousEvent.payload.currentPCM) {
      result.push({
        category: "closed_loop_pump_mode_changed",
        data: {
          mode: previousEvent.payload.currentPCM,
          previous_mode: previousEvent.payload.previousPCM,
        },
        date: latestEventDate.toISO() ?? "",
        sequence_id: previousEvent.eventSequenceNum,
      });
    }
    return result;
  };

  get_closed_loop_user_mode = (modeEvents: UserModeEvent[]) => {
    const result: PumpEventDTO[] = [];
    for (const event of modeEvents) {
      if (event.payload.currentUserMode) {
        result.push({
          category: "closed_loop_user_mode_changed",
          data: {
            mode: event.payload.currentUserMode,
            previous_mode: event.payload.previousUserMode,
          },
          date: event.timestamp.toISO() ?? "",
          sequence_id: event.eventSequenceNum,
        });
      }
    }
    return result;
  };

  transform = (data: TandemData): PumpEventDTO[] => {
    const pumpEvents = data.events.events.filter(
      e => this._is_cannula_filled_event(e) || this._is_tubing_filled_event(e) || this._is_cartridge_filled_event(e),
    );
    const dailyBasalEvents = data.events.events.filter(e => this._is_daily_basal_event(e) || this._is_new_day_event(e));
    const pumpModeEvents = data.events.events.filter(e => this._is_control_mode_change_event(e));
    const userModeEvents = data.events.events.filter(e => this._is_user_mode_change_event(e));
    const latestEvent = data.events.events.toSorted((a, b) => DateUtils.compareDateTimes(b.timestamp, a.timestamp))[0];
    console.log(latestEvent);
    if (!latestEvent) {
      return [];
    }

    const events = this.get_pump_events(pumpEvents);
    events.push(...this.get_daily_basal(dailyBasalEvents));
    events.push(...this.get_closed_loop_pump_mode(pumpModeEvents, latestEvent.timestamp));
    events.push(...this.get_closed_loop_user_mode(userModeEvents));

    return events;
  };
}
